import { ApiResponse } from "../api/api";
import { ModalType } from "../config/ModalTypes";
import { File } from "../types/db/schema/files/file.schema";
import { Host, getExcelVersion, isExcelVersionSupported } from "../util/versioning";
import useApi from "./useApi";
import useModal from "./useModal";
import { Buffer } from "buffer";

export default function useDownloads() {
  const callApi = useApi();
  const showModal = useModal();

  const downloadFile = async (downloadRequest: Promise<ApiResponse<File>>): Promise<boolean> => {
    try {
      const version = getExcelVersion();

      if (!version || version.host === Host.Browser) {
        let message = "Please open the addin inside of Microsoft Excel to access your downloads directly. Your download will still be processed.";
        message += "Note that this file requires Array Formulas to work, which might not be supported in the version of Microsoft Excel that you use.";

        showModal({
          type: ModalType.Information,
          text: message
        });
      }
      else if (!isExcelVersionSupported(version)) {
        showModal({
          text: `This file requires Array Formulas to work, which are introduced in version ${version.host === Host.Mac ? "'16.65'" : "'16.0.14326.20454'"} of Microsoft Excel. Your current version of Microsoft Excel does not support them.`,
          type: ModalType.Information
        })
      }
      else {
        showModal({
          text: "After downloading, make sure to save the file. If you get a Macro warning, please enable Macros via the File tab.",
          type: ModalType.Information
        })
      }

      const file = await callApi(downloadRequest);

      if (!file?.success || !file.data) {
        return false;
      }

      const {
        content,
        filename
      } = file.data;

      const fileContents = content.split(";base64,");

      if (!fileContents?.length || fileContents.length < 2) {

        showModal({
          type: ModalType.Error,
          text: "The downloaded file is faulty. Please try again"
        });

        return false;
      }

      if (version && version.isDesktop) {
        await Excel.run(async (context) => {
          await Excel.createWorkbook(fileContents[1]);
          return context.sync();
        }).catch(err => showModal({ type: ModalType.Error, text: err.message }));

        return true;
      }

      const blob = new Blob([Buffer.from(fileContents[1], 'base64') as any], { type: fileContents[0] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;

      link.setAttribute('download', filename);
      link.setAttribute('target', '_blank');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      return true;
    }
    catch (err) {
      console.log(err);

      showModal({
        type: ModalType.Error,
        text: "Something went wrong. Please try again."
      })

      return false;
    }
  }

  return downloadFile;
}